import { TimelineMode } from '@src/editor/commands/TimelineMode';
import { MediaCard, SidebarOption } from '@src/types.ts/general';
import { deepClone } from '@src/utility/deepClone';
import { LogManager } from '@src/utility/Logger';

import type { EditorStore } from '@src/editor';
import type { ElementState } from '@src/renderer/ElementState';
import type { Result } from '@src/services/common/Result';
import type TimelineStore from '@src/stores/TimelineStore';
import type { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';

// Result shape (currently only selection id; extend as needed)
export interface DeleteResultSelectionTarget {
  selectedId: string | null;
}

// Structural editor store shape (narrow to just what we use)
export interface EditorStoreLike extends Pick<EditorStore, 'editor'> {}

// Structural video creator store shape (subset only)
// NOTE: Using broader element types to avoid heavy coupling; fade producer expects concrete store.
export interface VideoCreatorStoreLike {
  time: number;
  selectedTrack: number;
  // Optional in concrete store; mark optional to prevent assignment errors when absent.
  selectedVolumeKeyPoint?: unknown;
  activeElementIds: string[];
  sidebarOptions: SidebarOption;
  mediaSubMenu: MediaCard;
  state?: { elements?: ElementState[] };
  // extraElementData map (raw shape tolerated, we only read & pass through)
  currentVideo?: { extraElementData?: Record<string, any> };
  getRealActiveElement: () => ElementState | undefined;
  setActiveElements: (id: string) => void;
  setTime: (t: number, a?: boolean) => Promise<void> | void;
  setDuration: (d: number | null) => Promise<void> | void;
  trackManager: {
    updateFrameLockedTracksAfterRearrange: (
      before: any[],
      after: any[],
    ) => void;
  };
  punchListManager: {
    removeIfPunchListItem: (id: string) => void;
    removePunchListItems: (ids: string[]) => void;
  };
  hasPhotoHighlight: (id: string) => boolean;
  clearExtraElementData: (ids: string[]) => void;
  // Present on the concrete VideoCreatorStore; optional here so this narrow
  // interface stays usable from contexts that don't supply it.
  deleteSelectedVolumeKeyPoint?: (elementId: string) => void;
}

export interface DeleteActiveElementServiceDeps {
  editorStore: EditorStoreLike;
  videoCreator: VideoCreatorStoreLike;
  timelineStore: TimelineStore;
  videoSource: VideoSourceStore;
}

/**
 * Service to delete the current active element / layout / track / karaoke / volume key point.
 * Mirrors prior imperative logic from TimelineControls; schedules selection of next video element.
 */
export class DeleteActiveElementService {
  private readonly _editor: EditorStoreLike;
  private readonly _creator: VideoCreatorStoreLike;
  private readonly _videoSource: VideoSourceStore;
  private readonly _timelineStore: TimelineStore;
  private readonly _logger = LogManager.getLogger(
    'service:DeleteActiveElementService',
  );

  constructor(deps: DeleteActiveElementServiceDeps) {
    this._editor = deps.editorStore;
    this._creator = deps.videoCreator;
    this._timelineStore = deps.timelineStore;
    this._videoSource = deps.videoSource;
  }

  private isIntroCard(elementId: string): boolean {
    const extraData = this._creator.currentVideo?.extraElementData;
    if (!extraData) return false;
    const meta = extraData[elementId];
    if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return false;
    const record = meta as Record<string, unknown>;
    return (
      record.compositionType === 'introCard' || record.placement === 'start'
    );
  }

  async removeActiveOrSelection(): Promise<
    Result<DeleteResultSelectionTarget>
  > {
    try {
      // Unified guard matching UI disabled state: prevent delete when
      // no element is selected AND no track is selected (selectedTrack === -1).
      // This ensures keyboard shortcut invocation also respects the same rule.
      const noElementSelected = !this._creator.activeElementIds?.length;
      if (noElementSelected && this._creator.selectedTrack === -1) {
        return {
          ok: false,
          error: {
            code: 'NOT_FOUND',
            message: 'No active selection to delete',
          },
        };
      }

      // we don't want to work with representational layout elements
      // const activeElement = this._creator.getRealActiveElement();
      const activeElement = this._timelineStore.getActiveTimelineElementIds()
        .length
        ? this._editor.editor.timeline.allElements.find(
            el =>
              el.id === this._timelineStore.getActiveTimelineElementIds()[0],
          )
        : undefined;

      // Volume key point deletion path. When a keypoint is selected, prefer
      // the timeline-store-derived activeElement (the audio track the user
      // clicked); fall back to the creator-store path. Critically, if a
      // keypoint is selected we MUST NOT fall through to the whole-element
      // delete branch — that would delete the entire audio track.
      if (this._creator.selectedVolumeKeyPoint) {
        const ownerId =
          activeElement?.id ?? this._creator.getRealActiveElement()?.source.id;
        if (ownerId) {
          this._creator.deleteSelectedVolumeKeyPoint?.(ownerId);
          return { ok: true, value: { selectedId: ownerId } };
        }
        // Keypoint selected but owner can't be resolved — bail rather than
        // delete the parent element.
        return {
          ok: false,
          error: {
            code: 'NOT_FOUND',
            message: 'Cannot resolve owner of selected volume key point',
          },
        };
      }

      if (activeElement) {
        // Single element deletion

        const initialSource = deepClone(this._videoSource.source);
        const deleteMode = this.isIntroCard(activeElement.id)
          ? TimelineMode.RIPPLE_ALL_TRACKS
          : TimelineMode.DEFAULT;
        this._editor.editor.commandRemoveElement(
          activeElement.id,
          true,
          deleteMode,
        );
        this._videoSource.onNextStateChange(async () => {
          this._creator.trackManager.updateFrameLockedTracksAfterRearrange(
            initialSource.elements,
            this._videoSource.source.elements,
          );
          this._creator.punchListManager.removeIfPunchListItem(
            activeElement.id,
          );
          this._creator.clearExtraElementData([activeElement.id]);
        });

        this._creator.punchListManager.removeIfPunchListItem(activeElement.id);
        this._creator.clearExtraElementData([activeElement.id]);
        if (activeElement.type === 'video') {
          await this._creator.setTime(activeElement.time, true);
        }
      } else if (this._creator.selectedTrack > -1) {
        // Track deletion

        const initialSource = deepClone(this._videoSource.source);
        const removedTrack = this._editor.editor.commandRemoveTrack(
          String(this._creator.selectedTrack),
        );
        this._videoSource.onNextStateChange(async () => {
          const removedIds = removedTrack?.elements?.map(el => el.id) || [];
          this._creator.trackManager.updateFrameLockedTracksAfterRearrange(
            initialSource.elements,
            this._videoSource.source.elements,
          );
          const idsWithPhotoHighlights = removedIds.filter(id =>
            this._creator.hasPhotoHighlight(id),
          );
          if (idsWithPhotoHighlights.length) {
            this._creator.punchListManager.removePunchListItems(
              idsWithPhotoHighlights,
            );
          }
          this._creator.clearExtraElementData(removedIds);
        });
      } else {
        // Nothing to delete
        return {
          ok: false,
          error: {
            code: 'NOT_FOUND',
            message: 'No active selection to delete',
          },
        };
      }

      // Default UI state adjustments & next selection scheduling
      // Sidebar state adjustments (optional, may be absent on structural type)
      this._creator.sidebarOptions = SidebarOption.media;
      this._creator.mediaSubMenu = MediaCard.photo;
      const elements = this._creator.state?.elements;
      if (elements?.length) {
        const videoElement = elements.find(el => el.source.type === 'video');
        if (videoElement?.source.id) {
          this._videoSource.onNextStateChange(() => {
            this._creator.setActiveElements(videoElement.source.id);
          });
          return { ok: true, value: { selectedId: videoElement.source.id } };
        }
      }
      return { ok: true, value: { selectedId: null } };
    } catch (err: unknown) {
      this._logger.error('Failed to delete active selection', { err });
      return {
        ok: false,
        error: {
          code: 'UNKNOWN',
          message: 'Unexpected delete failure',
          details: err ? String(err) : 'unknown',
        },
      };
    }
  }
}

// Re-export structural types for convenience (single export block only)
export type DeleteEditorStoreLike = EditorStoreLike;
export type DeleteVideoCreatorStoreLike = VideoCreatorStoreLike;
